import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function ConfirmSheet({
  isOpen,
  onClose,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'danger', // 'danger' | 'primary'
  onConfirm,
  onCancel = null,
  disabled = false
}) {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isAnimate, setIsAnimate] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsBusy(false);
      setIsMounted(true);
      const timer = setTimeout(() => {
        setIsAnimate(true);
      }, 15);
      return () => clearTimeout(timer);
    } else {
      setIsAnimate(false);
      const timer = setTimeout(() => {
        setIsMounted(false);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isMounted) return null;

  const renderDescription = () => {
    if (Array.isArray(description)) {
      let lines = [...description];
      if (lines.length === 3 && lines[2].includes('/')) {
        const parts = lines[2].replace(/[()]/g, '').split('/').map(s => s.trim());
        lines = [lines[0], lines[1], ...parts];
      }
      return (
        <div className="flex flex-col gap-0.5 mt-1">
          {lines.map((line, idx) => {
            let text = line;
            if (idx >= 2 && !text.startsWith('·')) {
              text = `· ${text}`;
            }
            let className = "";
            if (idx === 0) {
              className = "font-body-md font-bold text-[#1a1a1a] text-[14px] leading-snug";
            } else if (idx === 1) {
              className = "font-body-sm text-[#555555] text-[12.5px] leading-relaxed";
            } else {
              className = "font-body-sm text-[#888888] text-[11.5px] leading-relaxed pl-0.5";
            }
            return (
              <p key={idx} className={className}>
                {text}
              </p>
            );
          })}
        </div>
      );
    }
    return (
      <p className="font-body-md text-[#1a1a1a] font-bold text-[14px] mt-1 leading-snug whitespace-pre-line">
        {description}
      </p>
    );
  };

  // 확인 버튼 클릭 핸들러 (비동기 완료 대기 및 중복 실행 방지)
  const handleConfirm = async () => {
    if (isBusy || disabled) return;

    setIsBusy(true);
    try {
      const result = await onConfirm?.();
      if (result === false) {
        // 결과가 정확히 false이면 창을 닫지 않고 busy만 해제
        setIsBusy(false);
        return;
      }
      // 성공 시: onCancel이 없을 때만 onClose() 호출
      if (!onCancel) {
        onClose?.();
      }
    } catch (error) {
      // 오류 발생 시: 창을 닫지 않고 busy 해제 후 콘솔에 기록
      setIsBusy(false);
      console.error('[ConfirmSheet] onConfirm 처리 중 오류 발생:', error);
    }
  };

  return (
    <div 
      className={`fixed inset-0 z-[60] flex items-end justify-center p-4 pb-[calc(env(safe-area-inset-bottom,0px)+40px)] transition-all duration-250 ${isAnimate ? 'visible bg-black/20' : 'invisible bg-transparent'}`}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-[300px] bg-[#fbf9f1] rounded-2xl border-2 border-[#006565] overflow-hidden flex flex-col relative transition-all duration-250 ease-out shadow-lg ${isAnimate ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* 우측 상단 X(닫기) 버튼 */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-2.5 w-7 h-7 rounded-full flex items-center justify-center text-[#006565]/70 hover:bg-[#006565]/10 active:scale-90 transition-all z-10"
        >
          <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 0" }}>close</span>
        </button>
        
        {/* 헤더 (제목 및 본문) */}
        <div className="px-4 pt-3.5 pb-1 pr-9">
          <h2 className="font-headline-sm text-[17px] font-bold text-[#006565]">{title}</h2>
          {renderDescription()}
        </div>

        {/* 버튼 액션 영역 */}
        <div className="flex items-center justify-end gap-2 px-4 pt-2 pb-2.5 mt-1">
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                onClose();
              }
            }}
            className="px-4 py-1.5 rounded-full font-semibold text-[13px] bg-[#006565]/10 text-[#006565] hover:bg-[#006565]/20 active:scale-95 transition-colors disabled:opacity-50"
          >
            {cancelLabel ?? t('friends_sheet.cancel_default')}
          </button>
          <button
            type="button"
            disabled={disabled || isBusy}
            onClick={handleConfirm}
            className={`px-4 py-1.5 rounded-full font-semibold text-[13px] shadow-md active:scale-95 transition-colors disabled:opacity-50 ${
              isBusy ? 'cursor-not-allowed' : ''
            } ${
              confirmVariant === 'danger'
                ? 'bg-[#E53935] text-white hover:bg-[#D32F2F]'
                : 'bg-[#006565] text-white hover:bg-[#006565]/90'
            }`}
          >
            {confirmLabel ?? t('friends_sheet.confirm_default')}
          </button>
        </div>
      </div>
    </div>
  );
}



